import { urlApi } from './config';

export interface ErreurApi {
  erreur: string;
  code: string;
  details?: { recours?: string; blocages?: unknown[]; capacite?: string; [k: string]: unknown };
  status: number;
}

export class ApiError extends Error implements ErreurApi {
  constructor(
    public erreur: string, public code: string, public status: number,
    public details?: ErreurApi['details'],
  ) { super(erreur); this.name = 'ApiError'; }
}

const CLE = 'friends.token';
export const getToken = (): string | null => localStorage.getItem(CLE);
export const setToken = (t: string | null): void => {
  if (t) localStorage.setItem(CLE, t); else localStorage.removeItem(CLE);
};

/**
 * Panne de liaison avec le serveur.
 *
 * Distincte d'une ApiError : ici le serveur n'a pas repondu du tout. La
 * difference compte — un 401 doit deconnecter, une coupure de reseau non.
 */
export class PanneReseau extends Error {
  constructor(public readonly cause_: unknown) { super('Le serveur ne repond pas'); this.name = 'PanneReseau'; }
}

/* --- Signal global de liaison ---
 * Dix-huit ecrans sur dix-neuf chargent leurs donnees sans .catch et se gardent
 * derriere `if (!x) return <Squelette/>`. Sur une coupure, ils restaient donc en
 * gris, indefiniment, sans un mot. Plutot que d'ajouter un .catch a chacun — et
 * d'en oublier au prochain ecran — la couche d'appel previent une bonne fois. */
type Ecouteur = (enPanne: boolean) => void;
const ecouteurs = new Set<Ecouteur>();
let enPanne = false;

export function surLiaison(f: Ecouteur): () => void {
  ecouteurs.add(f); f(enPanne);
  return () => { ecouteurs.delete(f); };
}
function signaler(p: boolean): void {
  if (p === enPanne) return;
  enPanne = p;
  for (const f of ecouteurs) f(p);
}
export const liaisonRompue = (): boolean => enPanne;

/* --- Trace perdue ---
 * Le serveur repond avec X-Journal-Perdu quand une operation a reussi mais que
 * sa trace n'a pas pu etre ecrite. C'est le silence le plus couteux du systeme :
 * on croit avoir une preuve, on n'a rien. */
const ecouteursJournal = new Set<Ecouteur>();
let tracePerdue = false;
export function surJournal(f: Ecouteur): () => void {
  ecouteursJournal.add(f); f(tracePerdue);
  return () => { ecouteursJournal.delete(f); };
}
export const traceEstPerdue = (): boolean => tracePerdue;

async function requete<T>(methode: string, chemin: string, corps?: unknown): Promise<T> {
  const t = getToken();
  let r: Response;
  try {
    r = await fetch(urlApi(`/api${chemin}`), {
      method: methode,
      headers: {
        ...(corps ? { 'Content-Type': 'application/json' } : {}),
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: corps ? JSON.stringify(corps) : undefined,
    });
  } catch (e) {
    signaler(true);
    throw new PanneReseau(e);
  }
  signaler(false);
  if (r.headers.get('X-Journal-Perdu') === '1' && !tracePerdue) {
    tracePerdue = true;
    for (const f of ecouteursJournal) f(true);
  }

  if (r.status === 204) return undefined as T;
  const type = r.headers.get('content-type') ?? '';
  const donnees = type.includes('application/json') ? await r.json() : await r.text();

  if (!r.ok) {
    if (r.status === 401) setToken(null);
    const d = donnees as Partial<ErreurApi>;
    throw new ApiError(d.erreur ?? 'Erreur inattendue', d.code ?? 'ERREUR', r.status, d.details);
  }
  return donnees as T;
}

export const api = {
  get: <T>(c: string) => requete<T>('GET', c),
  post: <T>(c: string, b?: unknown) => requete<T>('POST', c, b ?? {}),
  patch: <T>(c: string, b?: unknown) => requete<T>('PATCH', c, b ?? {}),
  del: <T>(c: string) => requete<T>('DELETE', c),
};

export const qs = (o: Record<string, string | number | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};
